import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import UploadImageContainer from "../AddProduct/UploadImageContainer";
import ProductForm from "../AddProduct/ProductForm";
import SafetyTips from "../../common/ProductDetails/ProductItemDetail/ViewProduct/SafetyTips";
import { Modal } from "@mui/material";
import ItemUnderReviewModal from "../../Modal/ItemUnderReviewModal";
import { useDispatch, useSelector } from "react-redux";
import {
  closeItemUnderReviewModal,
  openItemUnderReviewModal,
} from "../../../store/actions/header";
import { scrollToTop } from "../../../shared/utility";
import {
  addProductPreview,
  addProductPreviewImages,
  updateUploadedImages,
  clearDeletedImages,
  editProduct,
  getProductDetails,
  getProductDetailsSuccess,
  getSingleProductImages,
  getSingleProductImagesSuccess,
  clearUploadedImages,
} from "../../../store/actions/products";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router";
import { CLEAR_PRODUCT_DETAILS_RIGHTS } from "../../../constants/constants";
import { EDIT_PRODUCT_SUCCESS } from "../../../store/actionTypes";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";

const EditProduct = () => {
  const dispatch: any = useDispatch();
  const navigate: any = useNavigate();
  const param: any = useParams();

  const [isEditProduct, setIsEditProduct] = useState<boolean>(false);

  const {
    profile,
    isItemUnderReviewModalOpen,
    singleProductDetails,
    singleProductImages,
    deletedImages,
    uploadedImages,
  } = useSelector((state: any) => ({
    isItemUnderReviewModalOpen: state?.header?.isItemUnderReviewModalOpen,
    singleProductDetails: state?.product?.singleProductDetails,
    singleProductImages: state?.product?.singleProductImages,
    profile: state?.profile?.profile,
    deletedImages: state?.product?.deletedImages,
    uploadedImages: state?.product?.uploadedImages,
  }));

  const { t } = useTranslation();

  const addProductFromValidationSchema = Yup.object().shape({
    productImages: Yup.object().nullable(),
    uploadedImages: Yup.array().nullable(),
    productName: Yup.string()
      .required(t("add_Product_From_Validation_Schema_1"))
      .trim()
      .min(2, t("add_Product_From_Validation_Schema_2"))
      .max(255, t("add_Product_From_Validation_Schema_2")),
    description: Yup.string()
      .required(t("add_Product_From_Validation_Schema_3"))
      .trim()
      .min(1, t("add_Product_From_Validation_Schema_4"))
      .max(4000, t("add_Product_From_Validation_Schema_4")),
    category: Yup.object()
      .test(
        "categoryTest",
        t("add_Product_From_Validation_Schema_5"),
        (value: any) => {
          if (value?.label) {
            return true;
          } else {
            return false;
          }
        }
      )
      .required(t("add_Product_From_Validation_Schema_5")),
    subCategory: Yup.object()
      .test(
        "subCategoryTest",
        t("add_Product_From_Validation_Schema_6"),
        (value: any) => {
          if (value?.label) {
            return true;
          } else {
            return false;
          }
        }
      )
      .required(t("add_Product_From_Validation_Schema_6")),
    price: Yup.number()
      .required(t("add_Product_From_Validation_Schema_7"))
      .min(0, t("add_Product_From_Validation_Schema_8")),
    location: Yup.object().required(t("add_Product_From_Validation_Schema_9")),
    negotiable: Yup.boolean().required(),
    isFormValid: Yup.boolean(),
  });

  const openModal = () => {
    dispatch(openItemUnderReviewModal());
  };

  const handleCloseModal = () => {
    dispatch(closeItemUnderReviewModal());
  };

  //Check whether all elements in the array are "" (empty strings).
  const emptyStringArray = (arr: string[]): boolean => {
    return arr.every((str: string) => str.trim() === "");
  };

  const handlePreview = (values: any) => {
    // seprate images and product data and updated in store
    let arrayOfImages: string[] = Object.values(values.productImages); //Array containing all uploaded images of the specific product.

    if (emptyStringArray(arrayOfImages) && !uploadedImages?.length) {
      toast.error(t("Notification.ERROR.UPLOAD_AN_IMAGE"));
    } else {
      const productId = JSON.parse(param?.id);
      const productData = {
        id: productId,
        text: values.description,
        price: values.price,
        title: values.productName,
        subcategory: values.subCategory,
        category: values.category,
        location: values.location,
        negotiablePrice: values.negotiable,
      };

      dispatch(addProductPreview(productData));

      dispatch(addProductPreviewImages(values.productImages));

      dispatch(updateUploadedImages(values.uploadedImages));

      navigate("/preview", { state: { lastVisit: "edit", productData } });
    }
  };

  const handleFormSubmit = async (values: any) => {
    const images = values.uploadedImages.map((item: any) => ({image: item,main: true}));
    let REQ = {
      contactMsisdn: profile.msisdn,
      contactName: profile.name.trim(),
      id: singleProductDetails?.id,
      locationId: values.location.value,
      negotiablePrice: values.negotiable,
      price: values.price,
      subcategoryId: values.subCategory.value,
      text: values.description,
      title: values.productName,
      oldImageIdList: deletedImages,
      images: images,
    };

    let res = await dispatch(editProduct(REQ));
    if (res.type === EDIT_PRODUCT_SUCCESS) {
      dispatch(openItemUnderReviewModal());
      toast.success(t("Notification.SUCCESS.ADD_PRODUCT_SUCCESS"));
      if (openModal) openModal();
    } else {
      toast.error(t("Notification.ERROR.ADD_PRODUCT_FAIL"));
      toast.error(res?.error);
    }
  };

  /* get single product image */
  useEffect(() => {
    const clear = CLEAR_PRODUCT_DETAILS_RIGHTS.reduce(
      (acc: boolean, item: string) => {
        if (window.location.pathname.includes(item)) {
          return true;
        }
        return acc;
      },false);
    setIsEditProduct(clear);

    if (
      singleProductDetails &&
      singleProductDetails?.imageIdList &&
      singleProductDetails?.imageIdList?.length > 0 &&
      profile
    ) {
      if (singleProductDetails?.imageIdList?.includes(",")) {
        const imageIdListArray = singleProductDetails?.imageIdList?.split(",");

        imageIdListArray?.forEach((element: string) => {
          const ImageId = JSON.parse(element);

          dispatch(getSingleProductImages(ImageId, profile?.type));
        });
      } else {
        const ImageId = JSON.parse(singleProductDetails?.imageIdList);
        dispatch(getSingleProductImages(ImageId, profile?.type));
      }
    }
    return () => {
      const clear = CLEAR_PRODUCT_DETAILS_RIGHTS.reduce(
        (acc: boolean, item: string) => {
          if (window.location.pathname.includes(item)) {
            return true;
          }
          return acc;
        },
        false
      );

      if (!clear) {
        dispatch(getProductDetailsSuccess(null));
        dispatch(getSingleProductImagesSuccess(null));
        dispatch(clearUploadedImages());
        dispatch(clearDeletedImages());
      }
    };
    // eslint-disable-next-line
  }, [singleProductDetails, profile]);

  /* get product details */
  useEffect(() => {
    if (param?.id) {
      const productId = JSON.parse(param?.id);
      if(profile && !singleProductDetails) dispatch(getProductDetails({ id: productId }, {profileType: profile?.type}));
    }
    // eslint-disable-next-line
  }, [profile]);

  useEffect(() => {
    scrollToTop();
  }, []);

  return (
    <section className="flex flex-col gap-3 px-3 lg:px-28 pt-20">
      <p className="text-[32px] font-medium">Edit Product</p>
      <Formik
        enableReinitialize
        initialValues={{
          productImages: singleProductImages || {},
          uploadedImages: uploadedImages || [],
          productName: singleProductDetails?.title || "",
          description: singleProductDetails?.text || "",
          price: singleProductDetails?.price || null,
          category: singleProductDetails?.category?.label
            ? singleProductDetails?.category
            : singleProductDetails?.categoryId
            ? {
                label: singleProductDetails?.category,
                value: singleProductDetails?.categoryId,
              }
            : {},
          subCategory: singleProductDetails?.subcategory?.label
            ? singleProductDetails?.subcategory
            : singleProductDetails?.subcategoryId
            ? {
                label: singleProductDetails?.subcategory,
                value: singleProductDetails?.subcategoryId,
              }
            : {},
          location: singleProductDetails?.location?.label
            ? singleProductDetails?.location
            : singleProductDetails?.locationId
            ? {
                label: singleProductDetails?.location,
                value: singleProductDetails?.locationId,
              }
            : null,
          negotiable:
            singleProductDetails == null
              ? true
              : singleProductDetails?.negotiablePrice,
          isFormValid: false,
          buttonName: "",
        }}
        validationSchema={addProductFromValidationSchema}
        onSubmit={(values) => {
          if (values.buttonName === "previewButton") {
            handlePreview(values);
          } else {
            handleFormSubmit(values);
          }
        }}
      >
        {({ values, errors, touched, setFieldValue }) => {
          return (
            <Form className="flex flex-col md:flex-row items-center md:items-start justify-center gap-10 w-full">
              <UploadImageContainer
                errors={errors}
                touched={touched}
                setFieldValue={setFieldValue}
                productImages={values.productImages}
                isEditProduct={isEditProduct}
                productId={singleProductDetails?.id}
                uploadedImages={values.uploadedImages}
              />
              <div className="flex flex-col flex-1 max-w-xl">
                <ProductForm
                  values={values}
                  errors={errors}
                  setFieldValue={setFieldValue}
                />
                <div className="w-full sm:w-full-60px border-t-[2px] border-grey-EF pb-20">
                  <SafetyTips preview={true} />
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
      <Modal
        open={isItemUnderReviewModalOpen}
        onClose={handleCloseModal}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
        }}
      >
        <ItemUnderReviewModal handleCloseModal={handleCloseModal} />
      </Modal>
    </section>
  );
};

export default EditProduct;
